import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Planner } from './pages/Planner';
import { Clients } from './pages/Clients';
import { ClientProfile } from './pages/ClientProfile';
import ClientPacks from "./pages/ClientPacks";
import { Requests } from './pages/Requests';
import { Services } from './pages/Services';
import { Staff } from './pages/Staff';
import { Centers } from './pages/Centers';
import { AuditLog } from './pages/AuditLog';
import { Settings } from './pages/Settings';
import { Dashboard } from './pages/Dashboard';
import { Waitlist } from './pages/Waitlist';
import { Notifications } from './pages/Notifications';
import { CostDashboard } from './pages/CostDashboard';
import { LeadScannerPage } from './pages/LeadScannerPage';
import { ContentEnginePage } from './pages/ContentEnginePage';
import { LeadDetail } from './pages/LeadDetail';
import WhatsAppInbox from './pages/WhatsAppInbox';
import BookingWidget from './pages/BookingWidget';
import { Toaster } from './components/ui/sonner';
import { Button, actionButtonClass } from './components/ui/button';
import { Loader2 } from 'lucide-react';
import InstallPWA from './components/InstallPWA';
import { ErrorBoundary } from './components/ErrorBoundary';
import {
  AUTH_VERIFICATION_KINDS,
  AUTH_IDENTITY_REASON_MESSAGES,
} from './lib/authIdentity';
import './App.css';

// Protected route wrapper
export const ProtectedRoute = ({ children, roles }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (roles && !roles.includes(user?.role)) {
    return <Navigate to="/planner" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public route wrapper (redirects if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={user?.role === 'SUPER_ADMIN' ? '/centers' : '/planner'} replace />;
  }

  return children;
};

export function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/reserva/:slug" element={<BookingWidget />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Root redirects to login */}
      <Route path="/" element={<Navigate to="/login" replace />} />

      {/* Protected routes */}
      <Route
        path="/planner"
        element={
          <ProtectedRoute>
            <Planner />
          </ProtectedRoute>
        }
      />
      
      <Route 
        path="/clients" 
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'CENTER_ADMIN']}>
            <Clients />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/clients/:id" 
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'CENTER_ADMIN']}>
            <ClientProfile />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/requests" 
        element={
          <ProtectedRoute>
            <Requests />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/services" 
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'CENTER_ADMIN']}>
            <Services />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/staff" 
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'CENTER_ADMIN']}>
            <Staff />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/centers" 
        element={
          <ProtectedRoute roles={['SUPER_ADMIN']}>
            <Centers />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/audit-log" 
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'CENTER_ADMIN']}>
            <AuditLog />
          </ProtectedRoute>
        } 
      />
      
      <Route 
        path="/waitlist" 
        element={
          <ProtectedRoute roles={['SUPER_ADMIN','CENTER_ADMIN','STAFF','CLIENT']}>
            <Waitlist />
          </ProtectedRoute>
        } 
      />

      <Route 
        path="/notifications" 
        element={
          <ProtectedRoute roles={['SUPER_ADMIN','CENTER_ADMIN','STAFF','CLIENT']}>
            <Notifications />
          </ProtectedRoute>
        } 
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'CENTER_ADMIN']}>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN', 'CENTER_ADMIN']}>
            <Settings />
          </ProtectedRoute>
        }
      />


      
      <Route 
        path="/client-packs" 
        element={
          <ProtectedRoute roles={['CLIENT']}>
            <ClientPacks />
          </ProtectedRoute>
        } 
      />

      <Route
        path="/admin/costs"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN']}>
            <CostDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/internal/ai-lead-scanner"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN']}>
            <LeadScannerPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/internal/content-engine"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN']}>
            <ContentEnginePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/internal/ai-lead-scanner/leads/:id"
        element={
          <ProtectedRoute roles={['SUPER_ADMIN']}>
            <LeadDetail />
          </ProtectedRoute>
        }
      />

      <Route
        path="/whatsapp-inbox"
        element={
          <ProtectedRoute roles={['CENTER_ADMIN', 'STAFF', 'SUPER_ADMIN']}>
            <WhatsAppInbox />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function AuthenticatedApp() {
  const { authVerificationError, retryAuth, escapeInvalidIdentity } = useAuth();

  if (authVerificationError) {
    // R5.3: el estado de verificación es visible y reintentable. El rechazo
    // DETERMINISTA de identidad (`invalid-identity`) NO es un problema de
    // conexión: se explica la causa real, la acción PRIMARIA es la salida
    // explícita a `/login` (volver a pedir lo mismo no va a cambiar el
    // resultado mientras la identidad siga sin ser válida) y "Reintentar"
    // queda como acción secundaria. El fallo TRANSITORIO (`unverified`)
    // conserva su mensaje, mantiene "Reintentar" como acción primaria y
    // ofrece también la salida explícita (que sólo invalida la credencial
    // si el usuario la pulsa).
    const invalidIdentity =
      authVerificationError.kind === AUTH_VERIFICATION_KINDS.INVALID_IDENTITY;
    const reason = authVerificationError.reason;
    const reasonMessage = reason ? AUTH_IDENTITY_REASON_MESSAGES[reason] : null;

    // Las dos acciones del panel. El componente `<Button>` compartido aporta
    // el estado de foco y de deshabilitado que las clases `btn-*` de
    // `index.css` no declaran; `type="button"` explícito porque el
    // componente no lo fija por defecto.
    //
    // D9 (enmienda 2026-10-29): la acción SECUNDARIA declara
    // `variant="secondary"`; la variante por defecto aporta
    // `bg-primary`/`text-primary-foreground` en la capa `utilities`, que
    // gana a `@layer components` y hacía que la secundaria se pintara igual
    // que la primaria. `actionButtonClass` restituye la métrica de `.btn-*`
    // y un anillo de foco que contrasta con el relleno.
    //
    // El CONJUNTO y el ORDEN de las acciones NO cambian con este arreglo
    // (decisión de la propietaria del proyecto): el panel `invalid-identity`
    // mantiene la salida explícita como acción PRIMARIA y el panel
    // `unverified` mantiene "Reintentar" como acción PRIMARIA.
    const retryAction = (
      <Button
        type="button"
        variant={invalidIdentity ? 'secondary' : 'default'}
        onClick={retryAuth}
        className={actionButtonClass(invalidIdentity ? 'btn-secondary' : 'btn-primary')}
        data-testid="auth-verification-retry-btn"
      >
        Reintentar
      </Button>
    );
    const escapeAction = (
      <Button
        type="button"
        variant={invalidIdentity ? 'default' : 'secondary'}
        onClick={escapeInvalidIdentity}
        className={actionButtonClass(invalidIdentity ? 'btn-primary' : 'btn-secondary')}
        data-testid="auth-verification-escape-btn"
      >
        Cerrar sesión e iniciar sesión de nuevo
      </Button>
    );

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-4 text-center">
          <div
            role="alert"
            data-testid="auth-verification-error"
            data-kind={authVerificationError.kind}
          >
            <h1 className="text-lg font-semibold">
              {invalidIdentity
                ? 'No hemos podido verificar tu identidad.'
                : 'No hemos podido verificar tu sesión.'}
            </h1>
            <p className="mt-2 text-sm text-zinc-400">
              {invalidIdentity
                ? reasonMessage ||
                  'La respuesta del servidor no contiene una identidad válida.'
                : 'Comprueba tu conexión e inténtalo de nuevo.'}
            </p>
            {invalidIdentity && reason ? (
              <p
                className="mt-2 text-xs text-zinc-500"
                data-testid="auth-verification-reason"
                data-reason={reason}
              >
                Motivo: {reason}
              </p>
            ) : null}
          </div>
          {/* D9/7.10: el ORDEN del DOM sigue la jerarquía visual. En el
              rechazo DETERMINISTA (`invalid-identity`) la acción primaria es
              la salida explícita a `/login` — reintentar el mismo payload no
              va a cambiar el resultado — así que va PRIMERO en el DOM. En el
              fallo TRANSITORIO (`unverified`) se mantiene el orden actual:
              "Reintentar" primero y la salida explícita como secundaria. */}
          {invalidIdentity ? escapeAction : retryAction}
          {invalidIdentity ? retryAction : escapeAction}
        </div>
      </div>
    );
  }

  return <AppRoutes />;
}

function App() {
  return (
    // R5.2/D9: el límite cubre TODO el árbol renderizado, no sólo el
    // subárbol de aplicación. `<Toaster>` e `<InstallPWA>` renderizaban
    // FUERA del límite, de modo que una excepción de render en cualquiera
    // de ellos desmontaba el árbol React completo y dejaba `#root` vacío
    // — justo lo que R5.2 prohíbe (enmienda de D9 del 2026-10-28).
    //
    // Enmienda de D9 del 2026-10-29: el límite se monta POR ENCIMA de
    // `<BrowserRouter>`, porque una excepción de render en la rama del
    // router (el proveedor de routing) también desmontaría el árbol React
    // completo y dejaría `#root` vacío. El fallback es un componente
    // estático: no usa `useAuth` ni hooks de router, así que puede mostrarse
    // aunque la rama que falló sea el router o el proveedor de
    // autenticación, y no expone stack traces, tokens ni PII.
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AuthenticatedApp />
          <Toaster
            position="bottom-right"
            toastOptions={{
              style: {
                background: '#18181B',
                border: '1px solid #27272A',
                color: '#FAFAFA',
              },
            }}
          />
          <InstallPWA />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
